import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';

/**
 * Catches lazy-import failures (and any other render-time errors) and
 * recovers by hard-reloading the page.
 *
 * The motivating case: every page in App.tsx is `lazy(() => import(...))`
 * and Vite emits hashed chunks. After a deploy, the *new* index.html
 * references new chunk hashes. A user with an *old* tab still holds the
 * old index.js, which references chunk URLs that no longer exist on disk.
 * Click a link → dynamic import → 404 → ChunkLoadError → React unmounts
 * the route → blank page. F5 fixes it because index.html is no-cache, so
 * the browser gets the new entry file and the new chunk URLs.
 *
 * This boundary makes that recovery automatic. We detect the canonical
 * chunk-load error variants (Vite/Webpack/native), then trigger one
 * reload. A `sessionStorage` flag prevents an infinite reload loop if
 * the post-reload page also throws — in that case we render the fallback
 * UI instead so the user sees something actionable.
 */

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

const RELOAD_FLAG = 'merkflow:chunk-reload-attempted';

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message ?? '';
  const name = error.name ?? '';
  return (
    name === 'ChunkLoadError' ||
    msg.includes('Loading chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  );
}

export class ChunkLoadErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Always log so we have a paper trail in the browser console for any
    // subsequent debugging — even when the auto-reload masks the symptom.
    console.error('[ChunkLoadErrorBoundary]', error, info.componentStack);

    if (isChunkLoadError(error)) {
      // Loop guard: if we already tried reloading once this session, don't
      // do it again. Otherwise a genuinely broken deploy would trap the
      // user in a refresh loop.
      const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG);
      if (!alreadyReloaded) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        return;
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return <ChunkLoadErrorFallback />;
    }
    return this.props.children;
  }
}

// Extracted as a hook-using functional component so the error boundary class
// (which can't call hooks) still renders translated fallback copy.
function ChunkLoadErrorFallback() {
  const { t } = useTranslation();
  return (
    <div className="bg-page text-text-primary flex min-h-screen items-center justify-center p-8">
      <div className="max-w-md text-center">
        <h1 className="font-heading text-2xl font-bold">{t('errors.chunkLoadError')}</h1>
        <p className="text-text-secondary mt-2 text-sm">{t('errors.chunkLoadHelp')}</p>
        <Button
          onClick={() => {
            sessionStorage.removeItem(RELOAD_FLAG);
            window.location.reload();
          }}
          className="mt-6"
        >
          {t('errors.reload')}
        </Button>
      </div>
    </div>
  );
}

// Clear the reload flag once a render successfully completes after the
// reload — see App.tsx mount-effect — so future deploys can recover too.
export function clearChunkReloadFlag() {
  sessionStorage.removeItem(RELOAD_FLAG);
}
